import { deepEqual } from 'fast-equals';
import { P, match } from 'ts-pattern';
import { Address } from 'viem';

import { mapStoryParagraphJsonToText } from 'components/story/StoryParagraph';

import {
  ApiMediaFragment,
  ApiStoryFragment,
  ApiStoryInventoryItemFragment,
  ApiStoryItemFragment,
} from 'gql/api/api-fragments.generated';
import { ApiStoryGroupInput } from 'gql/api/types-api.generated';
import { ChainId } from 'lib/chains';
import { mapEditionSaleConfigurationToEditionSale } from 'schemas/parse/edition-sale';
import {
  storyBlockquoteSchema,
  storyContentTypeSchemaMap,
  storyImageSchema,
  storyParagraphSchema,
  storyVideoSchema,
} from 'schemas/story';

import { EditionCollectionSale } from 'types/EditionSale';
import { ExtractMomentItemInventoryByType, MomentItem } from 'types/Moment';
import {
  StoryTextBlock,
  StoryBlock,
  StoryBlockquote,
  StoryGroup,
  StoryH1,
  StoryH2,
  StoryParagraph,
  StoryImageGroup,
  StoryImagePopulated,
  StoryImage,
  StoryImageEmpty,
  StoryImageAst,
  StoryTextGroup,
  StoryVideoGroup,
  StoryVideo,
  StoryVideoPopulated,
  StoryVideoEmpty,
  StoryVideoAst,
  StoryInventoryItemGroup,
  StoryInventoryItem,
  StoryInventoryItemPopulated,
  StoryInventoryItemEmpty,
} from 'types/Story';

export const mapStoryHeadingJsonToText = (json: StoryH1 | StoryH2): string => {
  return json.content[0].content[0].text;
};

export const mapStoryBlockquoteJsonToText = (json: StoryBlockquote): string => {
  const blockquoteContent = json.content[0].content;
  return blockquoteContent[0].content[0].text;
};

export const mapStoryBlockquoteJsonToCite = (json: StoryBlockquote): string => {
  const blockquoteContent = json.content[0].content;
  return blockquoteContent[1].content[0].content[0].text;
};

export const mapStoryTextBlockToText = (storyBlock: StoryTextBlock) => {
  return match(storyBlock)
    .with({ type: 'BLOCKQUOTE' }, (block) =>
      mapStoryBlockquoteJsonToText(block.content)
    )
    .with({ type: 'H1' }, (block) => mapStoryHeadingJsonToText(block.content))
    .with({ type: 'H2' }, (block) => mapStoryHeadingJsonToText(block.content))
    .with({ type: 'PARAGRAPH' }, (block) =>
      mapStoryParagraphJsonToText(block.content)
    )
    .exhaustive();
};

export const generateH1JsonFromText = (text: string): StoryH1 => {
  return {
    type: 'doc',
    content: [
      {
        type: 'heading',
        attrs: {
          level: 1,
        },
        content: [
          {
            type: 'text',
            text: text,
          },
        ],
      },
    ],
  };
};

export const generateH2JsonFromText = (text: string): StoryH2 => {
  return {
    type: 'doc',
    content: [
      {
        type: 'heading',
        attrs: {
          level: 2,
        },
        content: [
          {
            type: 'text',
            text: text,
          },
        ],
      },
    ],
  };
};

export const generateParagraphJsonFromText = (text: string): StoryParagraph => {
  return {
    type: 'doc',
    content: [
      {
        type: 'paragraph',
        content: [
          {
            type: 'text',
            text,
          },
        ],
      },
    ],
  };
};

export const generateBlockquoteJsonFromText = (options: {
  text: string;
  cite: string;
}): StoryBlockquote => {
  return {
    type: 'doc',
    content: [
      {
        type: 'blockquote',
        content: [
          {
            type: 'paragraph',
            content: [
              {
                type: 'text',
                text: options.text,
              },
            ],
          },
          {
            type: 'footer',
            content: [
              {
                type: 'cite',
                content: [
                  {
                    type: 'text',
                    text: options.cite,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  };
};

const mapApiStoryItemToStoryBlock = (
  item: ApiStoryItemFragment
): StoryBlock => {
  const storySchema = storyContentTypeSchemaMap;

  return match<ApiStoryItemFragment, StoryBlock>(item)
    .with(
      {
        type: 'H1',
        contentV2: {
          __typename: 'StoryItemJsonContent',
        },
      },
      (item) => ({
        id: item.id,
        type: item.type,
        content: storySchema[item.type].parse(item.contentV2.ast),
        placeholder: null,
      })
    )
    .with(
      {
        type: 'H2',
        contentV2: {
          __typename: 'StoryItemJsonContent',
        },
      },
      (item) => ({
        id: item.id,
        type: item.type,
        content: storySchema[item.type].parse(item.contentV2.ast),
        placeholder: null,
      })
    )
    .with(
      {
        type: 'PARAGRAPH',
        contentV2: {
          __typename: 'StoryItemJsonContent',
        },
      },
      (item) => ({
        id: item.id,
        type: item.type,
        content: storyParagraphSchema.parse(item.contentV2.ast),
        placeholder: null,
      })
    )
    .with(
      {
        type: 'BLOCKQUOTE',
        contentV2: {
          __typename: 'StoryItemJsonContent',
        },
      },
      (item) => ({
        id: item.id,
        type: item.type,
        content: storyBlockquoteSchema.parse(item.contentV2.ast),
        placeholder: null,
      })
    )
    .with(
      {
        type: 'IMAGE',
        contentV2: {
          __typename: 'StoryItemJsonContent',
        },
      },
      mapApiImageItemToReducerState
    )
    .with(
      {
        type: 'VIDEO',
        contentV2: {
          __typename: 'StoryItemJsonContent',
        },
      },
      mapApiVideoItemToReducerState
    )
    .with({ type: 'NFT' }, mapApiInventoryItemToReducerState)
    .with({ type: 'COLLECTION' }, mapApiInventoryItemToReducerState)
    .otherwise(() => {
      throw new Error(`Unknown story item type: ${item.type}`);
    });
};

export const mapApiStoryToReducerState = (
  story: ApiStoryFragment
): StoryGroup[] => {
  return story.groups
    .filter((group) => group.items.length > 0)
    .map((group) => {
      return {
        id: group.id,
        size: group.size,
        items: group.items.map(mapApiStoryItemToStoryBlock),
      };
    });
};

export const isTextStoryGroup = (
  group: StoryGroup
): group is StoryTextGroup => {
  return (
    group.items.every((item) => item.type === 'PARAGRAPH') ||
    group.items.every((item) => item.type === 'H1') ||
    group.items.every((item) => item.type === 'H2') ||
    group.items.every((item) => item.type === 'BLOCKQUOTE')
  );
};

export const isStoryImageGroup = (
  group: StoryGroup
): group is StoryImageGroup => {
  return group.items.every((item) => item.type === 'IMAGE');
};

export const isStoryVideoGroup = (
  group: StoryGroup
): group is StoryVideoGroup => {
  return group.items.every((item) => item.type === 'VIDEO');
};

export const isStoryInventoryItemGroup = (
  group: StoryGroup
): group is StoryInventoryItemGroup => {
  return group.items.every((item) => item.type === 'INVENTORY_ITEM');
};

function isValidStoryImageBlock(
  item: Extract<StoryBlock, { type: 'IMAGE' }>
): item is StoryImagePopulated {
  return item.type === 'IMAGE' && item.content.state === 'POPULATED';
}

function isValidStoryInventoryBlock(
  item: Extract<StoryBlock, { type: 'INVENTORY_ITEM' }>
): item is StoryInventoryItemPopulated {
  return item.type === 'INVENTORY_ITEM' && item.content.state === 'POPULATED';
}

function isValidStoryBlock(
  block: StoryBlock
): block is StoryTextBlock | StoryImagePopulated | StoryInventoryItemPopulated {
  if (block.type === 'IMAGE') {
    return isValidStoryImageBlock(block);
  }
  if (block.type === 'INVENTORY_ITEM') {
    return isValidStoryInventoryBlock(block);
  }
  return block.type !== 'VIDEO';
}

export const mapReducerStateToApiInput = (
  storyGroups: StoryGroup[]
): ApiStoryGroupInput[] => {
  return storyGroups.map((group) => {
    if (isTextStoryGroup(group)) {
      return {
        size: group.size,
        items: group.items
          .map((item) => ({
            type: item.type,
            content: item.content,
          }))
          .filter(isNotEmptyStoryBlock),
      };
    }

    if (isStoryVideoGroup(group)) {
      return {
        size: group.size,
        items: group.items.filter(isStoryVideoPopulated).map((item) => ({
          type: item.type,
          content: item.content.ast,
        })),
      };
    }

    return {
      size: group.size,
      items: group.items.filter(isValidStoryBlock).map((item) => {
        if (item.type === 'IMAGE') {
          return {
            type: 'IMAGE',
            content: item.content.ast,
          };
        }

        if (item.type === 'INVENTORY_ITEM') {
          return {
            type:
              item.content.inventoryItem.type === 'MARKET_NFT' ||
              item.content.inventoryItem.type === 'SOLD_NFT'
                ? 'NFT'
                : 'COLLECTION',
            referenceId: item.content.inventoryItem.id,
            content: {},
          };
        }

        return {
          type: item.type,
          content: item.content,
        };
      }),
    };
  });
};

const mapApiImageItemToReducerState = (
  item: ApiStoryItemFragment
): StoryImagePopulated => {
  // This is an unreachable state because we are checking for this __typename before the map
  if (item.contentV2.__typename !== 'StoryItemJsonContent') {
    throw new Error('Unknown story item type: Image');
  }
  const parsedImageItem = storyImageSchema.parse(item.contentV2.ast);

  // TODO (storytelling): should we put the transform into the zod schema?
  return {
    type: 'IMAGE',
    id: item.id,
    placeholder: null,
    content: {
      state: 'POPULATED',
      ast: parsedImageItem,
      base64Preview: null,
    },
  };
};

const mapApiVideoItemToReducerState = (
  item: ApiStoryItemFragment
): StoryVideoPopulated => {
  // This is an unreachable state because we are checking for this __typename before the map
  if (item.contentV2.__typename !== 'StoryItemJsonContent') {
    throw new Error('Unknown story item type: Video');
  }
  const parsedVideoItem = storyVideoSchema.parse(item.contentV2.ast);

  // TODO (storytelling): should we put the transform into the zod schema?
  return {
    type: 'VIDEO',
    id: item.id,
    placeholder: null,
    content: {
      state: 'POPULATED',
      ast: parsedVideoItem,
      width: parsedVideoItem.content[0].attrs.width,
      height: parsedVideoItem.content[0].attrs.height,
      src: parsedVideoItem.content[0].attrs.src,
    },
  };
};

const mapApiInventoryItemToReducerState = (
  item: ApiStoryItemFragment
): StoryInventoryItemPopulated => {
  if (item.contentV2.__typename === 'StoryItemInventoryContent') {
    return {
      type: 'INVENTORY_ITEM',
      id: item.id,
      placeholder: null,
      content: {
        state: 'POPULATED',
        inventoryItem: mapApiInventoryItemContentToReducerState(
          item.contentV2.item
        ),
      },
    };
  } else {
    throw new Error('NFT item must have a nft');
  }
};

export const isStoryImagePopulated = (
  item: StoryImage
): item is StoryImagePopulated => {
  return item.content.state === 'POPULATED';
};

export const isStoryImageEmpty = (
  item: StoryImage
): item is StoryImageEmpty => {
  return item.content.state === 'EMPTY';
};

export const mapImageAttributesToImageAst = (options: {
  src: string;
  caption: string;
}): StoryImageAst => {
  return {
    type: 'doc',
    content: [
      {
        type: 'figure',
        content: [
          {
            type: 'img',
            attr: {
              src: options.src,
            },
          },
          {
            type: 'figcaption',
            content: [
              {
                type: 'text',
                text: options.caption,
              },
            ],
          },
        ],
      },
    ],
  };
};

export const mapVideoAttributesToVideoAst = (options: {
  src: string;
  width: number;
  height: number;
}): StoryVideoAst => {
  return {
    type: 'doc',
    content: [
      {
        type: 'youtube',
        attrs: {
          src: options.src,
          width: options.width,
          height: options.height,
        },
      },
    ],
  };
};

export const isStoryVideoPopulated = (
  item: StoryVideo
): item is StoryVideoPopulated => {
  return item.content.state === 'POPULATED';
};

export const isStoryVideoEmpty = (
  item: StoryVideo
): item is StoryVideoEmpty => {
  return item.content.state === 'EMPTY';
};

export const isStoryInventoryItemPopulated = (
  item: StoryInventoryItem
): item is StoryInventoryItemPopulated => {
  return item.content.state === 'POPULATED';
};

export const isStoryInventoryEmpty = (
  item: StoryInventoryItem
): item is StoryInventoryItemEmpty => {
  return item.content.state === 'EMPTY';
};

// TODO (storytelling): call groupSizeToMaxWidthMap
export const groupSizeToWidthMap = {
  NONE: 740,
  SMALL: 740,
  MEDIUM: 960,
  LARGE: 1240,
} as const;

// TODO: add comments about this prosemirror quirk
export const EMPTY_BLOCK_CONTENT = {
  H1: generateH1JsonFromText(''),
  H2: generateH2JsonFromText(''),
  PARAGRAPH: generateParagraphJsonFromText('\u200B'),
  BLOCKQUOTE: generateBlockquoteJsonFromText({
    text: '',
    cite: '',
  }),
} as const;

export function isNotEmptyStoryBlock(
  block: Pick<StoryTextBlock, 'content' | 'type'>
) {
  return match(block)
    .with({ type: 'H1' }, (block) => {
      return !deepEqual(block.content, EMPTY_BLOCK_CONTENT.H1);
    })
    .with({ type: 'H2' }, (block) => {
      return !deepEqual(block.content, EMPTY_BLOCK_CONTENT.H2);
    })
    .with({ type: 'PARAGRAPH' }, (block) => {
      return !deepEqual(block.content, EMPTY_BLOCK_CONTENT.PARAGRAPH);
    })
    .with({ type: 'BLOCKQUOTE' }, (block) => {
      return !deepEqual(block.content, EMPTY_BLOCK_CONTENT.BLOCKQUOTE);
    })
    .exhaustive();
}

/**
 * Appends real quotes to a given string after removing any existing faux quotes.
 * Faux quotes include single quotes ('), backticks (`), and curly quotes (“, ”).
 * The function trims the input string, removes any faux quotes at the start or end,
 * and then appends real curly quotes.
 *
 * @param {string} input - The input string to be processed.
 * @returns {string} The processed string enclosed in real curly quotes.
 */
export function appendQuotes(input: string): string {
  // Trim whitespace before processing
  input = input.trim();

  // Define faux quotes, including the standard double quote
  const fauxQuotes = ["'", '`', '“', '”', '"'];

  // Create a regex pattern to match any of the faux quotes at the start or end
  const quoteRegex = new RegExp(
    `^[${fauxQuotes
      .map((q) => (['`', '"'].includes(q) ? `\\${q}` : q))
      .join('')}]+|[${fauxQuotes
      .map((q) => (['`', '"'].includes(q) ? `\\${q}` : q))
      .join('')}]+$`,
    'g'
  );

  // Strip off the faux quotes
  const strippedInput = input.replace(quoteRegex, '');

  // Append real curly quotes and return
  return `“${strippedInput}”`;
}

type MarketNft = ExtractMomentItemInventoryByType<'MarketNft'>;
type SoldNft = ExtractMomentItemInventoryByType<'SoldNft'>;
type DropCollection = ExtractMomentItemInventoryByType<'DropCollection'>;
type EditionCollection = ExtractMomentItemInventoryByType<'EditionCollection'>;

// TODO: deprecate this in favor of fragments
export type MappedNftMomentItem = {
  chainId: ChainId;
  id: string;
  contractAddress: Address;
  tokenId: number;
  name: string | null;
  media: ApiMediaFragment | null;
  saleStartsAt: MarketNft['saleStartsAt'] | null;
  activeOffer: MarketNft['activeOffer'] | null;
  activeBuyNow: MarketNft['activeBuyNow'] | null;
  activeAuction: MarketNft['activeAuction'] | null;
  creator: MarketNft['creator'];
};

// TODO: deprecate this in favor of fragments
export type MappedEditionCollectionMomentItem = {
  type: 'EDITION_COLLECTION';
  chainId: ChainId;
  contractAddress: Address;
  nftCount: number;
  name: string;
  id: string;
  creator: EditionCollection['creator'];
  slug: string;
  contractType: EditionCollection['contractType'];
  assetUrl: string | null;
  mimeType: string | null;
  sale: EditionCollectionSale;
};

// TODO: deprecate this in favor of fragments
export type MappedDropCollectionMomentItem = {
  type: 'DROP_COLLECTION';
  chainId: ChainId;
  contractAddress: Address;
  creator: DropCollection['creator'];
  name: string;
  id: string;
  slug: string;
  media: ApiMediaFragment | null;
  nftCount: number;
  saleConfiguration: DropCollection['saleConfiguration'];
  saleType: DropCollection['saleType'];
  clearingPrice: DropCollection['clearingPrice'];
  mintPrice: DropCollection['dropMintPrice'];
};

type MappedMarketNftMomentItem = MappedNftMomentItem & {
  type: 'MARKET_NFT';
};

type MappedSoldNftMomentItem = MappedNftMomentItem & {
  type: 'SOLD_NFT';
  sale: SoldNft['sale'];
};

export type MomentInventoryItem =
  | MappedMarketNftMomentItem
  | MappedSoldNftMomentItem
  | MappedEditionCollectionMomentItem
  | MappedDropCollectionMomentItem;

export const mapMomentItemsToNftItems = (
  momentItems: MomentItem[]
): MomentInventoryItem[] => {
  return momentItems.map((item) =>
    match(item)
      .with(
        {
          inventoryItem: { __typename: 'MarketNft' },
          tokenId: P.number,
        },
        (item) => ({
          ...item,
          chainId: item.inventoryItem.chainId,
          tokenId: item.tokenId,
          id: item.inventoryItem.marketNftId,
          name: item.inventoryItem.marketNftName,
          activeAuction: item.inventoryItem.activeAuction,
          activeBuyNow: item.inventoryItem.activeBuyNow,
          activeOffer: item.inventoryItem.activeOffer,
          saleStartsAt: item.inventoryItem.saleStartsAt,
          media: item.inventoryItem.media,
          creator: item.inventoryItem.creator,
          type: 'MARKET_NFT' as const,
        })
      )
      .with(
        { inventoryItem: { __typename: 'SoldNft' }, tokenId: P.number },
        (item) => ({
          ...item,
          chainId: item.inventoryItem.chainId,
          tokenId: item.tokenId,
          id: item.inventoryItem.soldNftId,
          name: item.inventoryItem.soldNftName,
          activeAuction: null,
          activeBuyNow: null,
          activeOffer: null,
          saleStartsAt: null,
          media: item.inventoryItem.media,
          creator: item.inventoryItem.creator,
          sale: item.inventoryItem.sale,
          type: 'SOLD_NFT' as const,
        })
      )
      .with(
        {
          inventoryItem: {
            __typename: 'EditionCollection',
            editionSale: P.not(P.nullish),
          },
        },
        (item) => ({
          ...item,
          chainId: item.inventoryItem.chainId,
          id: item.inventoryItem.editionId,
          contractAddress: item.inventoryItem.contractAddress,
          name: item.inventoryItem.editionName,
          creator: item.inventoryItem.creator,
          slug: item.inventoryItem.slug,
          contractType: item.inventoryItem.contractType,
          assetUrl: item.inventoryItem.assetUrl,
          mimeType: item.inventoryItem.mimeType,
          nftCount: item.inventoryItem.nftCount,
          sale: mapEditionSaleConfigurationToEditionSale(
            item.inventoryItem.editionSale
          ),
          type: 'EDITION_COLLECTION' as const,
        })
      )
      .with({ inventoryItem: { __typename: 'DropCollection' } }, (item) => ({
        ...item,
        chainId: item.inventoryItem.chainId,
        id: item.inventoryItem.dropId,
        contractAddress: item.inventoryItem.contractAddress,
        creator: item.inventoryItem.creator,
        name: item.inventoryItem.dropName,
        slug: item.inventoryItem.slug,
        clearingPrice: item.inventoryItem.clearingPrice,
        mintPrice: item.inventoryItem.dropMintPrice,
        saleType: item.inventoryItem.saleType,
        media: item.inventoryItem.media,
        nftCount: item.inventoryItem.nftCount,
        saleConfiguration: item.inventoryItem.saleConfiguration,
        type: 'DROP_COLLECTION' as const,
      }))
      .otherwise(() => {
        throw new Error('Invalid moment item');
      })
  );
};

export const mapApiInventoryItemContentToReducerState = (
  item: ApiStoryInventoryItemFragment
): MomentInventoryItem => {
  return match(item)
    .with({ __typename: 'MarketNft' }, (item) => ({
      ...item,
      type: 'MARKET_NFT' as const,
      inventoryItem: item,
      chainId: item.chainId,
    }))
    .with({ __typename: 'SoldNft' }, (item) => ({
      ...item,
      id: item.soldNftId,
      name: item.soldNftName,
      activeAuction: null,
      activeBuyNow: null,
      activeOffer: null,
      saleStartsAt: null,
      type: 'SOLD_NFT' as const,
      inventoryItem: item,
      chainId: item.chainId,
    }))
    .with({ __typename: 'EditionCollection' }, (item) => {
      if (!item.editionSale) {
        throw new Error('EditionCollection has no saleConfiguration');
      }

      return {
        ...item,
        id: item.editionId,
        name: item.editionName,
        type: 'EDITION_COLLECTION' as const,
        inventoryItem: item,
        chainId: item.chainId,
        sale: mapEditionSaleConfigurationToEditionSale(item.editionSale),
      };
    })
    .with({ __typename: 'DropCollection' }, (item) => ({
      ...item,
      id: item.dropId,
      contractAddress: item.contractAddress,
      saleType: null,
      clearingPrice: null,
      mintPrice: null,
      name: item.dropName,
      type: 'DROP_COLLECTION' as const,
      inventoryItem: item,
      chainId: item.chainId,
    }))
    .exhaustive();
};
